import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  Globe,
  Zap,
  Loader2,
  Check,
  Sparkles,
  Layout,
  Rocket,
  Lightbulb,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  MessageCircleQuestion,
  SkipForward,
} from 'lucide-react'
import { api, type ClarifyingQuestion } from '@/lib/api'
import { getStrategyLabel, cn } from '@/lib/utils'
import toast from 'react-hot-toast'

type Step = 'select' | 'analyze' | 'strategy' | 'questions' | 'generate' | 'preview'

const STEP_ORDER: Step[] = ['select', 'analyze', 'strategy', 'questions', 'generate', 'preview']

// LocalStorage key for persisting concept
const CONCEPT_STORAGE_KEY = 'unpark_concept_draft'

interface SavedConcept {
  concept: string
  domainId?: string
  savedAt: number
}

export function GenerateWizardPage() {
  const { domainId: paramDomainId } = useParams<{ domainId?: string }>()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<Step>(paramDomainId ? 'analyze' : 'select')
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(paramDomainId || null)
  const [selectedDomainName, setSelectedDomainName] = useState<string>('')
  const [concept, setConcept] = useState<string>('')
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null)
  const [questions, setQuestions] = useState<ClarifyingQuestion[]>([])
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [generatedSiteId, setGeneratedSiteId] = useState<string | null>(null)
  const [analysisData, setAnalysisData] = useState<any>(null)
  const [brief, setBrief] = useState({
    brandName: '',
    offer: '',
    audience: '',
    tone: '',
    location: '',
    proof: '',
    cta: '',
  })

  // Quiz state for questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)

  // Load saved concept from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(CONCEPT_STORAGE_KEY)
    if (saved) {
      try {
        const parsed: SavedConcept = JSON.parse(saved)
        // Only restore if less than 24 hours old
        if (Date.now() - parsed.savedAt < 24 * 60 * 60 * 1000) {
          setConcept(parsed.concept)
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [])

  // Save concept to localStorage when it changes
  useEffect(() => {
    if (concept.trim()) {
      const data: SavedConcept = {
        concept,
        domainId: selectedDomainId || undefined,
        savedAt: Date.now(),
      }
      localStorage.setItem(CONCEPT_STORAGE_KEY, JSON.stringify(data))
    }
  }, [concept, selectedDomainId])

  // Clear saved concept when successfully generated
  const clearSavedConcept = () => {
    localStorage.removeItem(CONCEPT_STORAGE_KEY)
  }

  // Fetch domains for selection
  const { data: domainsData, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.getDomains(),
    enabled: step === 'select',
  })

  // Only show verified domains — unverified domains cannot be deployed
  const availableDomains = domainsData?.domains?.filter(
    (d) => d.verification_status === 'verified'
  )

  // Analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: (domainId: string) => api.analyzeSite(domainId, concept || undefined),
    onSuccess: (data) => {
      setAnalysisData(data)
      setQuestions(data.questions)
      setSelectedStrategy(data.recommendedStrategy)
      setStep('strategy')
    },
    onError: (error: Error) => {
      toast.error(error.message)
      setStep('select')
    },
  })

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: () => {
      const customAnswers: Record<string, string> = {}
      questions.forEach((q, i) => {
        if (answers[i]) {
          customAnswers[q.question] = answers[i]
        }
      })
      if (brief.brandName) customAnswers._brandName = brief.brandName
      if (brief.offer) customAnswers._offer = brief.offer
      if (brief.audience) customAnswers._audience = brief.audience
      if (brief.tone) customAnswers._tone = brief.tone
      if (brief.location) customAnswers._location = brief.location
      if (brief.proof) customAnswers._proof = brief.proof
      if (brief.cta) customAnswers._cta = brief.cta
      // Include concept in custom answers if provided
      if (concept) {
        customAnswers['_concept'] = concept
      }
      return api.generateSite(selectedDomainId!, selectedStrategy || undefined, customAnswers)
    },
    onSuccess: (data) => {
      setGeneratedSiteId(data.site.id)
      setStep('preview')
      clearSavedConcept()
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      toast.success('Site generated!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  // Auto-analyze when domain is selected from URL
  useEffect(() => {
    if (paramDomainId && step === 'analyze' && !analyzeMutation.isPending && !analysisData) {
      analyzeMutation.mutate(paramDomainId)
    }
  }, [paramDomainId, step])

  const handleSelectDomain = (domainId: string, domainName: string) => {
    setSelectedDomainId(domainId)
    setSelectedDomainName(domainName)
    setStep('analyze')
    analyzeMutation.mutate(domainId)
  }

  const handleSelectStrategy = (strategy: string) => {
    setSelectedStrategy(strategy)
    if (questions.length > 0) {
      setCurrentQuestionIndex(0)
      setStep('questions')
    } else {
      setStep('generate')
      generateMutation.mutate()
    }
  }

  const handleAnswerQuestion = (answer: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestionIndex]: answer }))
  }

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else {
      // All questions answered, generate
      setStep('generate')
      generateMutation.mutate()
    }
  }

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    } else {
      // Go back to strategy selection
      setStep('strategy')
    }
  }

  const handleSkipAllQuestions = () => {
    setStep('generate')
    generateMutation.mutate()
  }

  // Navigate to a specific step
  const handleStepClick = (targetStep: Step) => {
    const currentIndex = STEP_ORDER.indexOf(step)
    const targetIndex = STEP_ORDER.indexOf(targetStep)

    if (targetIndex < currentIndex && targetStep !== 'analyze' && targetStep !== 'generate') {
      if (targetStep === 'select') {
        setSelectedDomainId(null)
        setSelectedDomainName('')
        setAnalysisData(null)
        setSelectedStrategy(null)
        setQuestions([])
        setAnswers({})
        setGeneratedSiteId(null)
        setCurrentQuestionIndex(0)
      } else if (targetStep === 'strategy') {
        setSelectedStrategy(null)
        setAnswers({})
        setGeneratedSiteId(null)
        setCurrentQuestionIndex(0)
      } else if (targetStep === 'questions') {
        setGeneratedSiteId(null)
        setCurrentQuestionIndex(0)
      }
      setStep(targetStep)
    }
  }

  const handleRestartWithConcept = () => {
    setAnalysisData(null)
    setSelectedStrategy(null)
    setQuestions([])
    setAnswers({})
    setGeneratedSiteId(null)
    setCurrentQuestionIndex(0)
    setBrief({
      brandName: '',
      offer: '',
      audience: '',
      tone: '',
      location: '',
      proof: '',
      cta: '',
    })
    setStep('select')
  }

  const steps = [
    { id: 'select' as Step, label: 'Select Domain', clickable: true },
    { id: 'analyze' as Step, label: 'Analyzing', clickable: false },
    { id: 'strategy' as Step, label: 'Strategy', clickable: true },
    { id: 'questions' as Step, label: 'Customize', clickable: true },
    { id: 'generate' as Step, label: 'Generating', clickable: false },
    { id: 'preview' as Step, label: 'Preview', clickable: false },
  ]

  const currentStepIndex = steps.findIndex((s) => s.id === step)
  const currentQuestion = questions[currentQuestionIndex]

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/sites" className="btn-ghost p-2">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold">Generate Site</h1>
          <p className="text-muted-foreground">
            AI-powered site generation wizard
          </p>
        </div>
        {step !== 'select' && step !== 'analyze' && (
          <button
            onClick={handleRestartWithConcept}
            className="btn-ghost text-sm"
          >
            <RotateCcw className="h-4 w-4" />
            Start Over
          </button>
        )}
      </div>

      {/* Progress - Clickable Steps */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => {
          const isCompleted = i < currentStepIndex
          const isCurrent = i === currentStepIndex
          const canClick = s.clickable && isCompleted

          return (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => canClick && handleStepClick(s.id)}
                disabled={!canClick}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-all',
                  isCurrent || isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                  canClick && 'hover:ring-2 hover:ring-primary/50 cursor-pointer',
                  !canClick && 'cursor-default'
                )}
                title={canClick ? `Go back to ${s.label}` : s.label}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  i + 1
                )}
              </button>
              {i < steps.length - 1 && (
                <div
                  className={cn(
                    'h-0.5 w-8 sm:w-12',
                    i < currentStepIndex ? 'bg-primary' : 'bg-muted'
                  )}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        {/* Step 1: Select Domain */}
        {step === 'select' && (
          <motion.div
            key="select"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Concept Input */}
            <div className="card border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold">Have a concept in mind?</h2>
                  <p className="text-sm text-muted-foreground">
                    Describe your idea and the AI will tailor the analysis and content to match.
                    Your concept is auto-saved.
                  </p>
                </div>
              </div>
              <textarea
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder="E.g., 'A news site with auto-updating SEO articles for AdSense monetization' or 'A SaaS tool for freelancers to track invoices' or 'An affiliate site reviewing productivity apps'..."
                className="input min-h-[100px] resize-none"
              />
              {concept && (
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <Check className="h-3 w-3 text-success" />
                  Concept saved - will be applied to your site
                </p>
              )}
            </div>

            {/* Domain Selection */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Select a Domain</h2>

              {domainsLoading ? (
                <div className="py-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                </div>
              ) : availableDomains?.length === 0 ? (
                <div className="py-8 text-center">
                  <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    No verified domains yet. Add a domain and connect it to
                    Cloudflare to get started.
                  </p>
                  <Link to="/dashboard/domains" className="btn-primary">
                    Go to Domains
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableDomains?.map((domain) => (
                    <button
                      key={domain.id}
                      onClick={() => handleSelectDomain(domain.id, domain.domain_name)}
                      className="w-full flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors text-left group"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Globe className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">{domain.domain_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {domain.aiAnalysis ? 'Analysis available' : 'Not analyzed yet'}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 2: Analyzing */}
        {step === 'analyze' && (
          <motion.div
            key="analyze"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="card text-center py-12"
          >
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Zap className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <div className="absolute inset-0 rounded-full border-4 border-primary/30 animate-ping" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">Analyzing Domain</h2>
            <p className="text-muted-foreground mb-2">
              AI is analyzing <span className="font-medium text-foreground">{selectedDomainName || 'your domain'}</span> for monetization potential...
            </p>
            {concept && (
              <p className="text-sm text-primary/80 italic">
                Using your concept: "{concept.slice(0, 60)}{concept.length > 60 ? '...' : ''}"
              </p>
            )}
          </motion.div>
        )}

        {/* Step 3: Strategy Selection */}
        {step === 'strategy' && analysisData && (
          <motion.div
            key="strategy"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">Analysis Complete</h2>
                <button
                  onClick={() => handleStepClick('select')}
                  className="btn-ghost btn-sm text-muted-foreground"
                >
                  <RotateCcw className="h-4 w-4" />
                  Change Concept
                </button>
              </div>
              {concept && (
                <p className="text-sm text-primary/80 italic mb-4">
                  Based on: "{concept.slice(0, 80)}{concept.length > 80 ? '...' : ''}"
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Niche</p>
                  <p className="font-medium">{analysisData.analysis.niche}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Target</p>
                  <p className="font-medium">{analysisData.analysis.targetAudience}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Commercial Intent</p>
                  <p className="font-medium">{analysisData.analysis.commercialIntent}/10</p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Choose a Strategy</h2>
              <div className="space-y-3">
                {analysisData.strategies.map((strategy: any, i: number) => (
                  <button
                    key={strategy.strategy}
                    onClick={() => handleSelectStrategy(strategy.strategy)}
                    className={cn(
                      'w-full flex items-start gap-4 p-4 rounded-lg border transition-colors text-left',
                      selectedStrategy === strategy.strategy
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-lg shrink-0',
                        i === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      )}
                    >
                      <Layout className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">
                          {getStrategyLabel(strategy.strategy)}
                        </p>
                        {i === 0 && (
                          <span className="badge badge-primary text-xs">
                            Recommended
                          </span>
                        )}
                        <span className="ml-auto text-sm text-primary font-medium">
                          {strategy.fitScore}% fit
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {strategy.reasoning}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Quiz-Style Questions */}
        {step === 'questions' && currentQuestion && (
          <motion.div
            key={`question-${currentQuestionIndex}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Question Progress */}
            <div className="card bg-gradient-to-br from-secondary/5 to-accent/5 border-secondary/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                    <MessageCircleQuestion className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Question {currentQuestionIndex + 1} of {questions.length}
                    </p>
                    <p className="font-semibold">Customize Your Site</p>
                  </div>
                </div>
                <button
                  onClick={handleSkipAllQuestions}
                  className="btn-ghost btn-sm text-muted-foreground"
                >
                  <SkipForward className="h-4 w-4" />
                  Skip All
                </button>
              </div>

              {/* Progress bar */}
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-secondary rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Creative Brief */}
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">Creative Brief (Optional)</h3>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label mb-1.5 block">Brand Name</label>
                  <input
                    type="text"
                    value={brief.brandName}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, brandName: e.target.value }))
                    }
                    placeholder="Acme Studios"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label mb-1.5 block">Primary Offer</label>
                  <input
                    type="text"
                    value={brief.offer}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, offer: e.target.value }))
                    }
                    placeholder="Free 30-minute strategy call"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label mb-1.5 block">Target Audience</label>
                  <input
                    type="text"
                    value={brief.audience}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, audience: e.target.value }))
                    }
                    placeholder="Busy founders in SaaS"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label mb-1.5 block">Tone / Voice</label>
                  <input
                    type="text"
                    value={brief.tone}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, tone: e.target.value }))
                    }
                    placeholder="Confident, direct, slightly playful"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label mb-1.5 block">Location / Service Area</label>
                  <input
                    type="text"
                    value={brief.location}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, location: e.target.value }))
                    }
                    placeholder="Austin, TX"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label mb-1.5 block">Preferred CTA</label>
                  <input
                    type="text"
                    value={brief.cta}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, cta: e.target.value }))
                    }
                    placeholder="Book a call"
                    className="input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="label mb-1.5 block">Proof / Credibility</label>
                  <input
                    type="text"
                    value={brief.proof}
                    onChange={(e) =>
                      setBrief((prev) => ({ ...prev, proof: e.target.value }))
                    }
                    placeholder="Trusted by 120+ founders, featured in TechCrunch"
                    className="input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                These details are optional, but they help the AI tailor copy and design.
              </p>
            </div>

            {/* Question Card */}
            <div className="card">
              <h2 className="text-xl font-semibold mb-6">{currentQuestion.question}</h2>

              <div className="space-y-3">
                {currentQuestion.options.map((option, i) => (
                  <motion.button
                    key={option}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => handleAnswerQuestion(option)}
                    className={cn(
                      'w-full px-5 py-4 rounded-xl border-2 text-left transition-all',
                      answers[currentQuestionIndex] === option
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'flex h-6 w-6 items-center justify-center rounded-full border-2 shrink-0 transition-colors',
                          answers[currentQuestionIndex] === option
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground'
                        )}
                      >
                        {answers[currentQuestionIndex] === option && (
                          <Check className="h-4 w-4" />
                        )}
                      </div>
                      <span className={cn(
                        'font-medium',
                        answers[currentQuestionIndex] === option && 'text-primary'
                      )}>
                        {option}
                      </span>
                    </div>
                  </motion.button>
                ))}

                {currentQuestion.allowCustom && (
                  <div className="pt-2">
                    <p className="text-sm text-muted-foreground mb-2">Or write your own:</p>
                    <input
                      type="text"
                      placeholder="Type your custom answer..."
                      value={
                        answers[currentQuestionIndex] &&
                        !currentQuestion.options.includes(answers[currentQuestionIndex])
                          ? answers[currentQuestionIndex]
                          : ''
                      }
                      onChange={(e) => handleAnswerQuestion(e.target.value)}
                      className="input"
                    />
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-border">
                <button
                  onClick={handlePrevQuestion}
                  className="btn-ghost"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {currentQuestionIndex === 0 ? 'Back to Strategy' : 'Previous'}
                </button>
                <button
                  onClick={handleNextQuestion}
                  className="btn-primary"
                >
                  {currentQuestionIndex === questions.length - 1 ? (
                    <>
                      Generate Site
                      <Sparkles className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Question dots navigation */}
            <div className="flex justify-center gap-2">
              {questions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentQuestionIndex(i)}
                  className={cn(
                    'h-2 rounded-full transition-all',
                    i === currentQuestionIndex
                      ? 'w-6 bg-primary'
                      : answers[i]
                      ? 'w-2 bg-primary/50'
                      : 'w-2 bg-muted'
                  )}
                  title={`Question ${i + 1}`}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 5: Generating */}
        {step === 'generate' && (
          <motion.div
            key="generate"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="card text-center py-12"
          >
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <div className="absolute inset-0 rounded-full border-4 border-primary/30 animate-spin" style={{ animationDuration: '3s' }} />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">Generating Your Site</h2>
            <p className="text-muted-foreground">
              AI is creating content and design for <span className="font-medium text-foreground">{selectedDomainName}</span>...
            </p>
            <div className="mt-4 max-w-xs mx-auto">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full animate-pulse w-2/3" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 6: Preview */}
        {step === 'preview' && generatedSiteId && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="card text-center py-12"
          >
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center">
                <Check className="h-8 w-8 text-success" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">Site Generated!</h2>
            <p className="text-muted-foreground mb-6">
              Your site for <span className="font-medium text-foreground">{selectedDomainName}</span> is ready.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to={`/dashboard/sites/${generatedSiteId}`}
                className="btn-primary"
              >
                <Rocket className="h-4 w-4" />
                View & Deploy
              </Link>
              <button
                onClick={handleRestartWithConcept}
                className="btn-outline"
              >
                <RotateCcw className="h-4 w-4" />
                Generate Another
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
